import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLocaleFromPath, isMarketingPath, normalizeMarketingPathname } from '../config/localeRoutes';
import {
  getMarketContentLangFromPath,
  isMarketPath,
  marketContentI18nLng,
} from '../config/marketRoutes';
import { normalizeLanguageCode } from '../config/normalizeLanguageCode';
import { languageService } from '../services/languageService';
import { markLanguageChoice } from '../utils/marketLanguage';

/**
 * Synchronise i18n avec le préfixe /es|/en|/de|/it ou le hub marché (/be/nl, /nl).
 * Persiste preferredLanguage sur les landings (#1452) pour survivre à signup → app.
 * Hors marketing : restaure la préférence localStorage (évite de rester bloqué
 * sur la langue d'une URL marketing après navigation vers /app).
 */
export default function LocaleRouteSync() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useLayoutEffect(() => {
    const pathname = normalizeMarketingPathname(location.pathname);
    const current = normalizeLanguageCode(i18n.language);
    const onMarketing = isMarketingPath(pathname) || isMarketPath(pathname);

    if (onMarketing) {
      const fromPrefix = getLocaleFromPath(pathname);
      const marketLang = isMarketPath(pathname) ? getMarketContentLangFromPath(pathname) : null;
      const pathLocale =
        fromPrefix ||
        (pathname === '/' ? 'fr' : null) ||
        (marketLang ? marketContentI18nLng(marketLang) : null) ||
        (isMarketingPath(pathname) && !isMarketPath(pathname) ? 'fr' : null);
      const resolvedPath = normalizeLanguageCode(pathLocale);
      if (resolvedPath) {
        languageService.setLocalLanguage(resolvedPath);
        markLanguageChoice('auto');
        if (current !== resolvedPath) {
          void i18n.changeLanguage(resolvedPath);
        }
      }
      return;
    }

    if (pathname.startsWith('/app')) {
      try {
        const preferred = normalizeLanguageCode(localStorage.getItem('preferredLanguage'));
        if (preferred && current !== preferred) {
          void i18n.changeLanguage(preferred);
        }
      } catch {
        /* ignore */
      }
      return;
    }

    // Auth / pages publiques hors marketing : ne plus forcer fr (#1452).
    // ?lang= est géré à l'init i18n + SignupUserPage / LoginPage.
    try {
      const preferred = normalizeLanguageCode(localStorage.getItem('preferredLanguage'));
      if (preferred && current !== preferred) {
        void i18n.changeLanguage(preferred);
      }
    } catch {
      /* ignore */
    }
  }, [location.pathname, i18n]);

  return null;
}
